// The standard way to show a slot's app buttons: one "Apps" menu holding all of
// them. A surface wanting something else — a single named app's button, buttons
// inline in a toolbar — calls `useSlotButtons` and renders its own control.

import { t } from '@lingui/core/macro';
import type { SlotId } from '@luminovo/apps-types';
import { MenuButton, MenuItem } from '@luminovo/design-system';
import { WorkflowIcon } from 'lucide-react';
import type { ReactElement } from 'react';

import { useSlotButtons } from '../hooks/useSlotButtons';

export interface AppButtonMenuProps {
    slotId: SlotId;
    /** What this slot hands every button it renders, as the slot registry declares it. */
    params: Readonly<Record<string, unknown>>;
    /** Locale used to resolve app-authored button names. */
    locale: string;
}

export function AppButtonMenu({ slotId, params, locale }: AppButtonMenuProps): ReactElement | null {
    const buttons = useSlotButtons(slotId, params, locale);

    // A tenant with no apps should see the surface it had before.
    if (buttons.length === 0) return null;

    return (
        <MenuButton
            appearance={'neutral'}
            size={'medium'}
            label={t`Apps`}
            icon={<WorkflowIcon />}
            isLoading={buttons.some((button) => button.isRunning)}
        >
            {buttons.map((button) => (
                <MenuItem key={button.key} label={button.name} onClick={button.run} />
            ))}
        </MenuButton>
    );
}
